import React, { useState, useEffect } from 'react';
import { Search, Download, CheckCircle2, RefreshCw } from 'lucide-react';
import {
  downloadCSV,
  convertTransactionsToCSV,
  fetchAdminPayments,
  fetchAdminDisputes,
  resolveAdminDispute,
} from '../utils/api';

export const PaymentsPage: React.FC = () => {
  const [tab, setTab] = useState<'transactions' | 'disputes'>('transactions');
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [transactionsList, setTransactionsList] = useState<any[]>([]);
  const [disputesList, setDisputesList] = useState<any[]>([]);
  const [metrics, setMetrics] = useState<any>({
    totalVolumeNGN: 0,
    successRate: '100%',
    openDisputes: 0,
  });
  const [loading, setLoading] = useState(true);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const [paymentsRes, disputesRes] = await Promise.all([
        fetchAdminPayments({ search: search.trim() || undefined, status: statusFilter !== 'all' ? statusFilter : undefined }),
        fetchAdminDisputes(),
      ]);

      if (paymentsRes?.transactions) {
        setTransactionsList(paymentsRes.transactions);
      }
      if (paymentsRes?.metrics) {
        setMetrics(paymentsRes.metrics);
      }
      if (disputesRes?.disputes) {
        setDisputesList(disputesRes.disputes);
      }
    } catch (e) {
      console.warn('Error loading payments:', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
    const handleModeChanged = () => {
      loadData();
    };
    window.addEventListener('system-mode-changed', handleModeChanged);
    return () => window.removeEventListener('system-mode-changed', handleModeChanged);
  }, [search, statusFilter]);

  const filteredTx = transactionsList;

  const handleExportCSV = () => {
    const csv = convertTransactionsToCSV(filteredTx);
    downloadCSV(`amoura-payments-${new Date().toISOString().slice(0, 10)}.csv`, csv);
    showToast(`✅ Exported ${filteredTx.length} payment transactions to CSV`);
  };

  const handleResolve = async (id: string, action: 'refund' | 'reject') => {
    setResolvingId(id);
    try {
      await resolveAdminDispute(id, action === 'refund' ? 'refund' : 'dismiss');
      setDisputesList((prev) =>
        prev.map((d) => (d.id === id ? { ...d, status: 'resolved', resolution: action === 'refund' ? `Refund processed via Squad` : 'Dispute dismissed' } : d))
      );
      showToast(action === 'refund' ? '✅ Refund dispatched to SquadCo via API' : 'Dispute dismissed via API');
    } catch {
      showToast('Action saved locally');
    } finally {
      setResolvingId(null);
    }
  };

  return (
    <div className="space-y-6">
      {/* ── Toast Notification ── */}
      {toastMessage && (
        <div className="fixed top-20 right-6 z-50 flex items-center gap-2.5 px-4 py-3 rounded-xl bg-[#141428] border border-emerald-500/40 text-white shadow-2xl shadow-emerald-500/10">
          <CheckCircle2 size={18} className="text-emerald-400 shrink-0" />
          <p className="text-xs font-bold">{toastMessage}</p>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-extrabold text-white font-['Outfit']">
            Payments & Squad Transactions
          </h1>
          <p className="text-sm text-zinc-400 mt-1">
            Monitor settlement webhooks, debit card processing, and dispute escalations
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => loadData()}
            className="flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold bg-[#141428] border border-[#1e1e3a] text-zinc-300 hover:text-white transition-colors cursor-pointer"
            title="Refresh transactions"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin text-pink-500' : ''} />
            Refresh
          </button>
          <button
            onClick={handleExportCSV}
            className="flex items-center gap-2 px-4 py-2 rounded-xl text-xs font-semibold bg-[#141428] border border-[#1e1e3a] text-zinc-300 hover:text-white transition-colors cursor-pointer"
            title="Export transactions as CSV"
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="p-5 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a]">
          <p className="text-xs font-bold text-zinc-400">Total Volume</p>
          <p className="text-2xl font-extrabold text-white font-['Outfit'] mt-1">
            ₦{(metrics?.totalVolumeNGN ?? 0).toLocaleString()}
          </p>
          <p className="text-xs text-emerald-400 mt-1">Via SquadCo settlement</p>
        </div>
        <div className="p-5 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a]">
          <p className="text-xs font-bold text-zinc-400">Successful TXNs</p>
          <p className="text-2xl font-extrabold text-emerald-400 font-['Outfit'] mt-1">
            {metrics?.successRate || '100%'}
          </p>
          <p className="text-xs text-zinc-400 mt-1">High settlement rate</p>
        </div>
        <div className="p-5 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a]">
          <p className="text-xs font-bold text-zinc-400">Open Disputes</p>
          <p className="text-2xl font-extrabold text-amber-400 font-['Outfit'] mt-1">
            {disputesList.filter((d) => d.status === 'open').length}
          </p>
          <p className="text-xs text-zinc-400 mt-1">Requires admin review</p>
        </div>
        <div className="p-5 rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a]">
          <p className="text-xs font-bold text-zinc-400">Dispute Ratio</p>
          <p className="text-2xl font-extrabold text-purple-400 font-['Outfit'] mt-1">
            {filteredTx.length > 0 ? `${Math.round((disputesList.length / filteredTx.length) * 100)}%` : '0%'}
          </p>
          <p className="text-xs text-zinc-400 mt-1">Reconciliation alerts</p>
        </div>
      </div>

      {/* Tabs & Table */}
      <div className="rounded-2xl bg-[#0d0d1a] border border-[#1e1e3a] overflow-hidden">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between border-b border-[#1e1e3a] px-4">
          <div className="flex gap-2">
            <button
              onClick={() => setTab('transactions')}
              className={`px-5 py-4 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
                tab === 'transactions'
                  ? 'border-pink-500 text-white'
                  : 'border-transparent text-zinc-400 hover:text-white'
              }`}
            >
              All Transactions ({transactionsList.length})
            </button>
            <button
              onClick={() => setTab('disputes')}
              className={`px-5 py-4 text-xs font-bold uppercase tracking-wider border-b-2 transition-colors ${
                tab === 'disputes'
                  ? 'border-pink-500 text-white'
                  : 'border-transparent text-zinc-400 hover:text-white'
              }`}
            >
              Disputes ({disputesList.filter((d) => d.status === 'open').length})
            </button>
          </div>

          {tab === 'transactions' && (
            <div className="flex items-center gap-3 py-3 sm:py-0">
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-400" />
                <input
                  type="text"
                  placeholder="Filter by name or ID..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-8 pr-3 py-1.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white placeholder-zinc-500 focus:outline-none"
                />
              </div>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-1.5 text-xs rounded-xl bg-[#141428] border border-[#1e1e3a] text-white focus:outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="success">Success</option>
                <option value="failed">Failed</option>
                <option value="disputed">Disputed</option>
              </select>
            </div>
          )}
        </div>

        {tab === 'transactions' ? (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead className="bg-[#141428] text-xs font-bold uppercase tracking-wider text-zinc-400 border-b border-[#1e1e3a]">
                <tr>
                  <th className="px-6 py-3">Reference</th>
                  <th className="px-6 py-3">Customer</th>
                  <th className="px-6 py-3">Plan</th>
                  <th className="px-6 py-3">Amount</th>
                  <th className="px-6 py-3">Payment Channel</th>
                  <th className="px-6 py-3">Status</th>
                  <th className="px-6 py-3">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#1e1e3a]">
                {filteredTx.length === 0 ? (
                  <tr>
                    <td colSpan={7} className="px-6 py-12 text-center text-xs text-zinc-500">
                      No payment transactions recorded in Cloudflare D1. Real customer transactions via Squad will display here in real time.
                    </td>
                  </tr>
                ) : (
                  filteredTx.map((tx) => (
                    <tr key={tx.id} className="hover:bg-[#141428]/50 transition-colors">
                      <td className="px-6 py-4 font-mono text-xs font-bold text-white">{tx.id}</td>
                      <td className="px-6 py-4">
                        <p className="font-semibold text-white">{tx.user}</p>
                        <p className="text-xs text-zinc-400">{tx.phone}</p>
                      </td>
                      <td className="px-6 py-4 text-xs font-semibold text-purple-400">{tx.plan}</td>
                      <td className="px-6 py-4 font-bold text-emerald-400">{tx.amount}</td>
                      <td className="px-6 py-4 text-xs text-zinc-300">{tx.method}</td>
                      <td className="px-6 py-4">
                        <span
                          className={`px-2.5 py-1 text-xs font-bold rounded-lg ${
                            tx.status === 'success'
                              ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
                              : tx.status === 'disputed'
                              ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
                              : 'bg-red-500/10 text-red-400 border border-red-500/20'
                          }`}
                        >
                          {tx.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-zinc-400">{tx.date}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="p-6 space-y-4">
            {disputesList.length === 0 ? (
              <div className="py-12 text-center text-xs text-zinc-500">
                No open or escalated payment disputes found in database.
              </div>
            ) : (
              disputesList.map((d) => (
              <div key={d.id} className="p-5 rounded-2xl bg-[#141428] border border-[#1e1e3a] flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <div className="flex items-center gap-3 mb-1">
                    <span className="font-mono text-xs font-bold text-pink-400">{d.id}</span>
                    <span className="font-bold text-white text-base">{d.user}</span>
                    <span className="text-xs font-bold text-emerald-400">{d.amount}</span>
                  </div>
                  <p className="text-xs text-zinc-300 mb-1">{d.reason}</p>
                  {d.resolution && <p className="text-xs text-emerald-400 font-semibold">✓ {d.resolution}</p>}
                </div>

                {d.status === 'open' && (
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => handleResolve(d.id, 'refund')}
                      disabled={resolvingId === d.id}
                      className="px-4 py-2 text-xs font-bold rounded-xl bg-emerald-500 text-white hover:bg-emerald-600 transition-colors shadow-sm"
                    >
                      Issue Squad Refund
                    </button>
                    <button
                      onClick={() => handleResolve(d.id, 'reject')}
                      disabled={resolvingId === d.id}
                      className="px-4 py-2 text-xs font-bold rounded-xl bg-zinc-800 text-zinc-300 hover:text-white hover:bg-zinc-700 transition-colors"
                    >
                      Dismiss Claim
                    </button>
                  </div>
                )}
                </div>
              ))
            )}
          </div>
        )}
      </div>
    </div>
  );
};
